import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, ShieldCheck, Trash2, Archive, KeyRound } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api, { formatApiError } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { BUREAU_ROLES, COTISATION_LABELS, PERMISSIONS, formatDateTime } from "@/constants";

const EMPTY_FORM = { email: "", first_name: "", last_name: "", phone: "", cotisation_status: "a_jour", membership_active: true, bureau_role: "", directory_consent: false };

const ROLE_LABELS = { adherent: "Adhérent", admin: "Admin", super_admin: "Super-admin" };

export default function AdminMembers() {
  const { user: currentUser } = useAuth();
  const [members, setMembers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [tempPassword, setTempPassword] = useState(null);
  const [saving, setSaving] = useState(false);
  const [membersArchive, setMembersArchive] = useState([]);
  const [motifTarget, setMotifTarget] = useState(null);
  const [motif, setMotif] = useState("");
  const [archiving, setArchiving] = useState(false);
  const [permOpen, setPermOpen] = useState(false);
  const [permMemberId, setPermMemberId] = useState("");
  const [permChecked, setPermChecked] = useState([]);
  const [permSaving, setPermSaving] = useState(false);

  const isSuperAdmin = currentUser?.role === "super_admin";

  const load = () => {
    api.get("/admin/members").then((r) => setMembers(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/members-archive").then((r) => setMembersArchive(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setTempPassword(null); setDialogOpen(true); };
  const openEdit = (m) => {
    setEditing(m);
    setTempPassword(null);
    setForm({ email: m.email, first_name: m.first_name, last_name: m.last_name, phone: m.phone,
      cotisation_status: m.cotisation_status, membership_active: m.membership_active,
      bureau_role: m.bureau_role || "", directory_consent: m.directory_consent });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const { email, ...payload } = form;
        if (!isSuperAdmin) delete payload.bureau_role;
        await api.patch(`/admin/members/${editing.id}`, payload);
        toast.success("Adhérent mis à jour");
      } else {
        const { data } = await api.post("/admin/members", form);
        setTempPassword(data.temporary_password);
        toast.success("Adhérent créé");
      }
      await load();
      if (editing) setDialogOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (m) => {
    try {
      await api.patch(`/admin/members/${m.id}`, { is_active: !m.is_active });
      toast.success(m.is_active ? "Compte désactivé" : "Compte réactivé");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const changeRole = async (m, role) => {
    try {
      await api.patch(`/admin/members/${m.id}/role`, { role });
      toast.success(`Rôle mis à jour : ${ROLE_LABELS[role]}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const archiveMember = async () => {
    if (!motifTarget || motif.trim().length < 2) return;
    setArchiving(true);
    try {
      await api.post(`/admin/members/${motifTarget.id}/archive`, { motif: motif.trim() });
      toast.success("Adhérent supprimé et archivé avec motif.");
      setMotifTarget(null);
      setMotif("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setArchiving(false);
    }
  };

  const bureauMembers = members.filter((m) => m.role !== "adherent");
  const permMember = bureauMembers.find((m) => m.id === permMemberId) || null;

  const openPermissions = () => { setPermMemberId(""); setPermChecked([]); setPermOpen(true); };
  const selectPermMember = (id) => {
    setPermMemberId(id);
    const m = members.find((x) => x.id === id);
    setPermChecked(m?.permissions ?? PERMISSIONS.map((p) => p.key));
  };
  const togglePerm = (key) => setPermChecked((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]));
  const savePermissions = async () => {
    if (!permMemberId) return;
    setPermSaving(true);
    try {
      await api.patch(`/admin/members/${permMemberId}/permissions`, { permissions: permChecked });
      toast.success("Droits mis à jour");
      setPermOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPermSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-members-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="admin-members-title">Adhérents</h1>
          <p className="text-sm text-slate-500 mt-1">{members.length} compte(s) enregistré(s).</p>
        </div>
        <Button className="bg-sky-700 hover:bg-sky-800" onClick={openCreate} data-testid="member-create-btn">
          <Plus className="h-4 w-4 mr-2" />Ajouter un adhérent
        </Button>
        {isSuperAdmin && (
          <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={openPermissions} data-testid="permissions-open-btn">
            <KeyRound className="h-4 w-4 mr-2" />Droits du bureau
          </Button>
        )}
      </div>

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <Table data-testid="members-table">
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>N° adhérent</TableHead>
                <TableHead>Cotisation</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Rôle</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((m) => {
                const isProtected = m.role === "super_admin" && m.id !== currentUser?.id;
                return (
                <TableRow key={m.id} data-testid={`member-row-${m.id}`} className={isProtected ? "opacity-60 bg-slate-50/60" : ""}>
                  <TableCell className="font-medium">
                    {m.first_name} {m.last_name}
                    {m.bureau_role && <span className="block text-xs text-slate-500">{BUREAU_ROLES[m.bureau_role]}</span>}
                  </TableCell>
                  <TableCell className="text-sm">{m.email}</TableCell>
                  <TableCell className="text-sm" data-testid={`member-number-${m.id}`}>
                    {m.membership_number ? (
                      <span>
                        <span className="font-medium text-slate-700">{m.membership_number}</span>
                        {m.membership_campaign && <span className="block text-xs text-slate-400">{m.membership_campaign}</span>}
                      </span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={m.cotisation_status === "a_jour"
                      ? "bg-emerald-100 text-emerald-800 border border-emerald-200 hover:bg-emerald-100"
                      : "bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-100"}>
                      {COTISATION_LABELS[m.cotisation_status] || m.cotisation_status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={m.is_active
                      ? (m.membership_active ? "bg-sky-100 text-sky-800 border border-sky-200 hover:bg-sky-100" : "bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-100")
                      : "bg-red-100 text-red-800 border border-red-200 hover:bg-red-100"}>
                      {!m.is_active ? "Désactivé" : m.membership_active ? "Actif" : "À valider"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {isSuperAdmin ? (
                      <Select value={m.role} onValueChange={(v) => changeRole(m, v)} data-testid={`member-role-select-${m.id}`}>
                        <SelectTrigger className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="adherent">Adhérent</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="super_admin">Super-admin</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        {m.role !== "adherent" && <ShieldCheck className="h-3.5 w-3.5 text-sky-700" />}
                        {ROLE_LABELS[m.role]}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right space-x-2">
                    {isProtected ? (
                      <span className="inline-flex items-center gap-1 text-xs text-slate-400 italic" data-testid={`member-protected-${m.id}`}>
                        <ShieldCheck className="h-3.5 w-3.5" />Protégé
                      </span>
                    ) : (
                      <>
                        <Button variant="outline" size="sm" onClick={() => openEdit(m)} data-testid={`member-edit-${m.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => toggleActive(m)} data-testid={`member-toggle-${m.id}`}>
                          {m.is_active ? "Désactiver" : "Activer"}
                        </Button>
                        <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => { setMotifTarget(m); setMotif(""); }} data-testid={`member-delete-${m.id}`}
                          aria-label="Supprimer avec motif">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="member-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Modifier l'adhérent" : "Nouvel adhérent"}</DialogTitle>
            <DialogDescription>Fiche adhérent : coordonnées, cotisation et accès à l'espace membre.</DialogDescription>
          </DialogHeader>
          {tempPassword ? (
            <div className="space-y-4" data-testid="member-temp-password">
              <p className="text-sm text-slate-600">
                Compte créé. Communiquez ce mot de passe provisoire à l'adhérent (il pourra le changer
                via « Mot de passe oublié ») :
              </p>
              <p className="font-mono text-lg bg-slate-100 rounded-lg p-4 text-center select-all" data-testid="member-temp-password-value">
                {tempPassword}
              </p>
              <Button className="w-full" onClick={() => setDialogOpen(false)} data-testid="member-dialog-close-btn">Fermer</Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Prénom</Label>
                  <Input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="member-form-firstname" />
                </div>
                <div className="space-y-2">
                  <Label>Nom</Label>
                  <Input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="member-form-lastname" />
                </div>
              </div>
              {!editing && (
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="member-form-email" />
                </div>
              )}
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="member-form-phone" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Cotisation</Label>
                  <Select value={form.cotisation_status} onValueChange={(v) => setForm({ ...form, cotisation_status: v })}>
                    <SelectTrigger data-testid="member-form-cotisation"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="a_jour">À jour</SelectItem>
                      <SelectItem value="en_attente">En attente</SelectItem>
                      <SelectItem value="expiree">Expirée</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {isSuperAdmin && (
                  <div className="space-y-2">
                    <Label>Rôle au bureau</Label>
                    <Select value={form.bureau_role} onValueChange={(v) => setForm({ ...form, bureau_role: v })}>
                      <SelectTrigger data-testid="member-form-bureau-role"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value=" ">—</SelectItem>
                        <SelectItem value="president">Président·e</SelectItem>
                        <SelectItem value="vice_president">Vice-président</SelectItem>
                        <SelectItem value="vice_presidente">Vice-présidente</SelectItem>
                        <SelectItem value="tresorier">Trésorier·ère</SelectItem>
                        <SelectItem value="secretaire">Secrétaire</SelectItem>
                        <SelectItem value="membre_bureau">Membre du bureau</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3">
                <Checkbox id="member-active" checked={form.membership_active}
                  onCheckedChange={(v) => setForm({ ...form, membership_active: !!v })} data-testid="member-form-active" />
                <Label htmlFor="member-active" className="font-normal text-sm">Adhésion validée (accès à l'espace membre)</Label>
              </div>
              <div className="flex items-center gap-3">
                <Checkbox id="member-consent" checked={form.directory_consent}
                  onCheckedChange={(v) => setForm({ ...form, directory_consent: !!v })} data-testid="member-form-consent" />
                <Label htmlFor="member-consent" className="font-normal text-sm">Consentement annuaire (RGPD)</Label>
              </div>
              <Button type="submit" className="w-full bg-sky-700 hover:bg-sky-800" disabled={saving} data-testid="member-form-submit">
                {saving ? "Enregistrement…" : editing ? "Enregistrer" : "Créer le compte"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={permOpen} onOpenChange={setPermOpen}>
        <DialogContent data-testid="permissions-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Droits du bureau</DialogTitle>
            <DialogDescription>
              Sélectionnez un membre du bureau puis cochez les sections auxquelles il a accès. Le super-admin conserve tous les droits.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Select value={permMemberId} onValueChange={selectPermMember}>
              <SelectTrigger data-testid="permissions-member-select"><SelectValue placeholder="Choisir un membre du bureau…" /></SelectTrigger>
              <SelectContent>
                {bureauMembers.map((m) => (
                  <SelectItem key={m.id} value={m.id} data-testid={`permissions-member-option-${m.id}`}>
                    {m.first_name} {m.last_name} — n° {m.membership_number || "—"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {permMember && permMember.role === "super_admin" && (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                Le super-admin a tous les droits, ils ne sont pas modifiables.
              </p>
            )}
            {permMember && permMember.role !== "super_admin" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5" data-testid="permissions-checkboxes">
                {PERMISSIONS.map((p) => (
                  <label key={p.key} className="flex items-center gap-2.5 text-sm text-slate-700 bg-slate-50 rounded-lg px-3 py-2.5 cursor-pointer" data-testid={`perm-${p.key}`}>
                    <Checkbox checked={permChecked.includes(p.key)} onCheckedChange={() => togglePerm(p.key)} />
                    {p.label}
                  </label>
                ))}
              </div>
            )}
            <Button className="w-full bg-red-700 hover:bg-red-800" onClick={savePermissions}
              disabled={!permMember || permMember.role === "super_admin" || permSaving} data-testid="permissions-save-btn">
              {permSaving ? "Enregistrement…" : "Enregistrer les droits"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {membersArchive.length > 0 && (
        <div className="pt-2" data-testid="members-archive-section">
          <h2 className="text-lg font-bold tracking-tight text-slate-700 flex items-center gap-2">
            <Archive className="h-5 w-5 text-slate-400" />Anciens comptes supprimés (archive)
          </h2>
          <div className="mt-3 space-y-2">
            {membersArchive.map((a, i) => (
              <div key={a.id || i} className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs text-slate-500" data-testid={`member-archived-${i}`}>
                <span className="font-semibold text-slate-600">{a.first_name} {a.last_name}</span>
                {a.membership_number ? ` · n° ${a.membership_number}` : ""} — supprimé le {formatDateTime(a.archived_at)} par {a.archived_by}
                · <span className="italic">motif : {a.motif}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!motifTarget} onOpenChange={(o) => { if (!o) setMotifTarget(null); }}>
        <DialogContent data-testid="member-archive-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Supprimer cet adhérent</DialogTitle>
            <DialogDescription>
              {motifTarget ? `${motifTarget.first_name} ${motifTarget.last_name} — le compte sera archivé avec le motif (traçabilité) puis supprimé.` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="member-archive-motif">Motif de la suppression *</Label>
              <Input id="member-archive-motif" value={motif} onChange={(e) => setMotif(e.target.value)}
                placeholder="Ex. : démission, doublon, demande de l'adhérent…" data-testid="member-archive-motif-input" />
            </div>
            <div className="flex gap-2.5">
              <Button className="bg-red-600 hover:bg-red-700" onClick={archiveMember}
                disabled={archiving || motif.trim().length < 2} data-testid="member-archive-confirm-btn">
                {archiving ? "Suppression…" : "Supprimer avec ce motif"}
              </Button>
              <Button variant="outline" onClick={() => setMotifTarget(null)}>Annuler</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
